'use client'

import { useCallback, useEffect, useRef, useState, lazy, Suspense } from 'react'
import Image from 'next/image'
import { useSearchParams } from 'next/navigation'
import { useTranslations } from 'next-intl'
import { useSession } from 'next-auth/react'
import { useRouter } from '@/i18n/navigation'
import { AppIcon } from '@/components/ui/icons'
import { Link } from '@/i18n/navigation'
import LanguageSwitcher from '@/components/LanguageSwitcher'
import { HomeWorkspacePreloader } from '@/components/auth/HomeWorkspacePreloader'

const SignInModal = lazy(() => import('@/components/auth/SignInModal').then(m => ({ default: m.SignInModal })))

type AuthModalMode = 'signin'

const LANDING_VIDEO_BASE_URL = (process.env.NEXT_PUBLIC_LANDING_VIDEO_BASE_URL || '').replace(/\/+$/, '')

function getLandingVideoSrc(fileName: string): string {
  return LANDING_VIDEO_BASE_URL ? `${LANDING_VIDEO_BASE_URL}/${fileName}` : `/video/${fileName}`
}

function getLandingPosterSrc(videoSrc: string): string {
  const fileName = videoSrc.split('/').pop()?.replace(/\.mp4(?:\?.*)?$/i, '.webp')
  return fileName ? `/video/${fileName}` : ''
}

function shouldPreferPosterOnlyVideo(): boolean {
  if (typeof window === 'undefined') return false
  const connection = (navigator as Navigator & { connection?: { saveData?: boolean } }).connection
  return connection?.saveData === true
    || window.matchMedia('(max-width: 767px), (hover: none), (pointer: coarse)').matches
}

interface LazyHomeVideoProps {
  src: string
  poster?: string
  className?: string
  autoPlay?: boolean
  loop?: boolean
  muted?: boolean
  playsInline?: boolean
  pauseOnLeave?: boolean
  priority?: 'high' | 'low'
}

function LazyHomeVideo({
  src,
  poster,
  className,
  autoPlay = true,
  loop = true,
  muted = true,
  playsInline = true,
  pauseOnLeave = false,
  priority = 'high',
}: LazyHomeVideoProps) {
  const videoRef = useRef<HTMLVideoElement | null>(null)
  const [shouldLoad, setShouldLoad] = useState(false)
  const [isVisible, setIsVisible] = useState(false)
  const [deferVideoLoad, setDeferVideoLoad] = useState(false)
  const posterSrc = poster ?? getLandingPosterSrc(src)

  const threshold = priority === 'high' ? 0.1 : 0.5

  useEffect(() => {
    setDeferVideoLoad(shouldPreferPosterOnlyVideo())
  }, [])

  useEffect(() => {
    const node = videoRef.current
    if (!node) return

    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsVisible(entry.isIntersecting)
        if (entry.isIntersecting && !shouldLoad && !deferVideoLoad) {
          setShouldLoad(true)
          if (autoPlay && !pauseOnLeave) {
            setTimeout(() => {
              if (videoRef.current) {
                const playPromise = videoRef.current.play()
                if (playPromise) {
                  void playPromise.catch(() => undefined)
                }
              }
            }, 50)
          }
        }
      },
      { threshold }
    )

    observer.observe(node)
    return () => observer.disconnect()
  }, [autoPlay, deferVideoLoad, pauseOnLeave, shouldLoad, threshold])

  const handleInteraction = useCallback(() => {
    if (!shouldLoad) setShouldLoad(true)
    if (videoRef.current) {
      videoRef.current.currentTime = 0
      const playPromise = videoRef.current.play()
      if (playPromise) {
        void playPromise.catch(() => undefined)
      }
    }
  }, [shouldLoad])

  const handleLeave = useCallback(() => {
    if (videoRef.current) {
      videoRef.current.pause()
      videoRef.current.currentTime = 0
    }
  }, [])

  return (
    <div className={`relative w-full h-full bg-gradient-to-br from-gray-900/50 to-gray-800/30 ${className || ''}`}>
      <video
        ref={videoRef}
        src={shouldLoad ? src : undefined}
        poster={posterSrc || undefined}
        autoPlay={isVisible && shouldLoad && autoPlay && !pauseOnLeave}
        loop={loop}
        muted={muted}
        playsInline={playsInline}
        preload="none"
        className="absolute inset-0 w-full h-full object-cover"
        onPointerEnter={handleInteraction}
        onFocus={handleInteraction}
        onTouchStart={handleInteraction}
        onPointerLeave={pauseOnLeave ? handleLeave : undefined}
      />
      {/* Loading shimmer */}
      {!shouldLoad && !posterSrc && (
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="w-8 h-8 border-2 border-white/20 border-t-white/60 rounded-full animate-spin" />
        </div>
      )}
    </div>
  )
}

function AutoPlayCarouselVideo({
  src,
  poster,
  className,
}: {
  src: string
  poster?: string
  className?: string
}) {
  const videoRef = useRef<HTMLVideoElement | null>(null)
  const [isVisible, setIsVisible] = useState(false)
  const [shouldLoad, setShouldLoad] = useState(false)
  const [deferVideoLoad, setDeferVideoLoad] = useState(false)
  const posterSrc = poster ?? getLandingPosterSrc(src)

  useEffect(() => {
    setDeferVideoLoad(shouldPreferPosterOnlyVideo())
  }, [])

  useEffect(() => {
    const node = videoRef.current
    if (!node || !shouldLoad) return
    const playPromise = node.play()
    if (playPromise) {
      void playPromise.catch(() => undefined)
    }
  }, [shouldLoad])

  useEffect(() => {
    const node = videoRef.current
    if (!node) return

    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsVisible(entry.isIntersecting)
        if (entry.isIntersecting && !shouldLoad && !deferVideoLoad) {
          setShouldLoad(true)
        }
      },
      { threshold: 0.1 }
    )

    observer.observe(node)
    return () => observer.disconnect()
  }, [deferVideoLoad, shouldLoad])

  const handleInteraction = useCallback(() => {
    if (!shouldLoad) setShouldLoad(true)
  }, [shouldLoad])

  return (
    <div className={`relative w-full h-full bg-gradient-to-br from-gray-900/50 to-gray-800/30 ${className || ''}`}>
      <video
        ref={videoRef}
        src={shouldLoad ? src : undefined}
        poster={posterSrc || undefined}
        autoPlay={isVisible && shouldLoad}
        loop
        muted
        playsInline
        preload="none"
        className="absolute inset-0 w-full h-full object-cover"
        onPointerEnter={handleInteraction}
        onFocus={handleInteraction}
        onTouchStart={handleInteraction}
      />
      {!shouldLoad && !posterSrc && (
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="w-8 h-8 border-2 border-white/20 border-t-white/60 rounded-full animate-spin" />
        </div>
      )}
    </div>
  )
}

export default function Home() {
  const t = useTranslations('landing')
  const { status } = useSession()
  const router = useRouter()
  const searchParams = useSearchParams()
  const [isLoaded, setIsLoaded] = useState(false)
  const [scrollY, setScrollY] = useState(0)
  const [authModalMode, setAuthModalMode] = useState<AuthModalMode | null>(null)
  const authMode = searchParams?.get('auth') ?? null
  const authError = searchParams?.get('error') ?? null
  const showSignInModal = status !== 'authenticated' && authModalMode === 'signin'

  useEffect(() => {
    setIsLoaded(true)
  }, [])

  useEffect(() => {
    let frameId = 0
    const handleScroll = () => {
      if (frameId) return
      frameId = window.requestAnimationFrame(() => {
        frameId = 0
        const rawScrollY = window.scrollY
        const nextScrollY = rawScrollY > 500 ? 501 : rawScrollY > 0 ? 1 : 0
        setScrollY((current) => (current === nextScrollY ? current : nextScrollY))
      })
    }
    window.addEventListener('scroll', handleScroll, { passive: true })
    return () => {
      window.removeEventListener('scroll', handleScroll)
      if (frameId) window.cancelAnimationFrame(frameId)
    }
  }, [])

  useEffect(() => {
    if (status === 'authenticated') {
      router.replace({ pathname: '/workspace' })
    }
  }, [status, router])

  useEffect(() => {
    if (authMode === 'signin' || authError === 'RateLimited') {
      setAuthModalMode('signin')
      return
    }

    if (authMode === 'signup') {
      setAuthModalMode('signin')
      return
    }

    setAuthModalMode(null)
  }, [authMode, authError])

  const openAuthModal = useCallback((mode: AuthModalMode) => {
    if (typeof window === 'undefined') return
    setAuthModalMode(mode)
    const url = new URL(window.location.href)
    url.searchParams.set('auth', mode)
    url.searchParams.delete('error')
    window.history.pushState(null, '', `${url.pathname}${url.search}${url.hash}`)
  }, [])

  const closeAuthModal = useCallback(() => {
    if (typeof window === 'undefined') return
    setAuthModalMode(null)
    const url = new URL(window.location.href)
    url.searchParams.delete('auth')
    url.searchParams.delete('error')
    window.history.replaceState(null, '', `${url.pathname}${url.search}${url.hash}`)
  }, [])

  if (status === 'authenticated') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-black">
        <div className="flex flex-col items-center gap-4">
          <Image
            src="/logo-loading.webp?v=1"
            alt="XFX"
            width={120}
            height={120}
            unoptimized
            className="animate-pulse"
          />
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-black font-sans" style={{ overflowX: 'hidden' }}>
      <HomeWorkspacePreloader enabled={false} />
      <div className="fixed inset-0 z-0">

        <div className="absolute inset-0 bg-[linear-gradient(rgba(0,255,255,0.03)_1px,transparent_1px),linear-gradient(90deg,rgba(0,255,255,0.03)_1px,transparent_1px)] bg-[size:60px_60px] [mask-image:radial-gradient(ellipse_80%_50%_at_50%_0%,black_70%,transparent_100%)]" />
        
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-cyan-500/20 rounded-full blur-[128px] animate-pulse" />
        <div className="absolute top-1/4 right-1/4 w-80 h-80 bg-purple-500/20 rounded-full blur-[128px] animate-pulse" style={{ animationDelay: '1s' }} />
        <div className="absolute bottom-1/4 left-1/2 w-96 h-96 bg-blue-500/15 rounded-full blur-[128px] animate-pulse" style={{ animationDelay: '2s' }} />
      </div>

      <header
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ease-[cubic-bezier(0.44,0,0.56,1)] ${isLoaded ? 'opacity-100' : 'opacity-0'}`}
        style={{
          backgroundColor: scrollY > 0 ? 'rgba(15, 17, 21, 0.4)' : 'transparent',
          backdropFilter: scrollY > 0 ? 'blur(16px)' : 'none',
          WebkitBackdropFilter: scrollY > 0 ? 'blur(16px)' : 'none',
        }}
      >
        <nav className="flex items-center justify-between px-6 md:px-8 py-4 max-w-7xl mx-auto">
          <Link href={{ pathname: '/' }} className="flex items-center gap-3 group">
            <div className="relative">
              <Image
                src="/logo.webp?v=1"
                alt="XFX"
                width={40}
                height={40}
                unoptimized
                className="transition-transform group-hover:scale-110"
              />
              <div className="absolute inset-0 bg-cyan-400/30 rounded-full blur-md opacity-0 group-hover:opacity-100 transition-opacity" />
            </div>
            <span className="text-xl font-bold bg-gradient-to-r from-cyan-400 via-purple-400 to-pink-400 bg-clip-text text-transparent">
              {t('title')}
            </span>
          </Link>

          <div className="flex items-center gap-4">
            <button
              type="button"
              onClick={() => openAuthModal('signin')}
              className="px-5 py-2 text-sm font-medium text-gray-300 hover:text-white transition-colors"
            >
              {t('signin')}
            </button>
            <LanguageSwitcher />
          </div>
        </nav>
      </header>

      <section className="relative min-h-screen flex items-center justify-center px-4 pt-20">
        <div className="max-w-6xl mx-auto text-center relative z-10">
          <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-full border border-cyan-500/30 bg-cyan-500/10 mb-8 transition-all duration-700 ${isLoaded ? 'translate-y-0 opacity-100' : 'translate-y-4 opacity-0'}`}>
            <span className="relative flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-cyan-400 opacity-75" />
              <span className="relative inline-flex rounded-full h-2 w-2 bg-cyan-400" />
            </span>
            <span className="text-cyan-400 text-sm font-medium">{t('badge')}</span>
          </div>

          <h1 className={`text-5xl md:text-7xl lg:text-8xl font-black mb-8 leading-tight transition-all duration-700 delay-100 ${isLoaded ? 'translate-y-0 opacity-100' : 'translate-y-8 opacity-0'}`}>
            <span className="block text-white mb-2">{t('heroTitle')}</span>
            <span className="block bg-gradient-to-r from-cyan-400 via-purple-400 to-pink-400 bg-clip-text text-transparent bg-[length:200%_auto] animate-gradient-x">
              {t('heroSubtitle')}
            </span>
          </h1>

          <p className={`text-lg md:text-xl text-gray-400 max-w-2xl mx-auto mb-10 leading-relaxed transition-all duration-700 delay-200 ${isLoaded ? 'translate-y-0 opacity-100' : 'translate-y-8 opacity-0'}`}>
            {t('heroDesc')}
          </p>

          <div className={`flex flex-col sm:flex-row items-center justify-center gap-4 transition-all duration-700 delay-300 ${isLoaded ? 'translate-y-0 opacity-100' : 'translate-y-8 opacity-0'}`}>
            <button
              type="button"
              onClick={() => openAuthModal('signin')}
              className="group relative px-8 py-4 text-lg font-bold text-black rounded-xl overflow-hidden"
            >
              <span className="absolute inset-0 bg-gradient-to-r from-cyan-400 via-purple-400 to-pink-400 group-hover:scale-105 transition-transform duration-300" />
              <span className="relative flex items-center gap-2">
                {t('startCreating')}
                <AppIcon name="arrowRight" className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </span>
            </button>
            <button
              type="button"
              onClick={() => document.getElementById('video-showcase')?.scrollIntoView({ behavior: 'smooth', block: 'start' })}
              className="group flex items-center gap-2 px-8 py-4 text-lg font-medium text-white rounded-xl border border-white/20 hover:border-cyan-500/50 hover:bg-white/5 transition-all duration-300"
            >
              <AppIcon name="play" className="w-5 h-5" />
              {t('watchDemo')}
            </button>
          </div>

          <div className={`grid grid-cols-3 gap-8 max-w-xl mx-auto mt-16 pt-8 border-t border-white/10 transition-all duration-700 delay-400 ${isLoaded ? 'translate-y-0 opacity-100' : 'translate-y-8 opacity-0'}`}>
            <div className="text-center">
              <div className="text-3xl md:text-4xl font-bold bg-gradient-to-r from-cyan-400 to-blue-400 bg-clip-text text-transparent">{t('stat1Value')}</div>
              <div className="text-gray-500 text-sm mt-1">{t('stat1Label')}</div>
            </div>
            <div className="text-center">
              <div className="text-3xl md:text-4xl font-bold bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text text-transparent">{t('stat2Value')}</div>
              <div className="text-gray-500 text-sm mt-1">{t('stat2Label')}</div>
            </div>
            <div className="text-center">
              <div className="text-3xl md:text-4xl font-bold bg-gradient-to-r from-pink-400 to-red-400 bg-clip-text text-transparent">{t('stat3Value')}</div>
              <div className="text-gray-500 text-sm mt-1">{t('stat3Label')}</div>
            </div>
          </div>
        </div>
      </section>

      <section className="relative py-32 px-4">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-20">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
              {t('coreFeatures')}
            </h2>
            <p className="text-gray-400 text-lg max-w-2xl mx-auto">
              {t('featuresDesc')}
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              { icon: 'userCircle' as const, title: t('features.character.title'), desc: t('features.character.description'), color: 'from-cyan-500 to-blue-500', delay: '0' },
              { icon: 'fileText' as const, title: t('features.storyboard.title'), desc: t('features.storyboard.description'), color: 'from-purple-500 to-pink-500', delay: '100' },
              { icon: 'globe' as const, title: t('features.world.title'), desc: t('features.world.description'), color: 'from-green-500 to-emerald-500', delay: '200' },
              { icon: 'play' as const, title: t('features.video.title'), desc: t('features.video.description'), color: 'from-orange-500 to-red-500', delay: '300' },
              { icon: 'audioWave' as const, title: t('features.audio.title'), desc: t('features.audio.description'), color: 'from-pink-500 to-rose-500', delay: '400' },
              { icon: 'sparkles' as const, title: t('features.editing.title'), desc: t('features.editing.description'), color: 'from-indigo-500 to-purple-500', delay: '500' },
            ].map((feature) => (
              <div
                key={feature.title}
                className="group relative p-6 rounded-2xl bg-gradient-to-b from-white/5 to-transparent border border-white/10 hover:border-cyan-500/50 transition-all duration-500 hover:scale-[1.02] hover:bg-gradient-to-b hover:from-white/[0.08]"
              >
                <div className={`absolute inset-0 rounded-2xl bg-gradient-to-br ${feature.color} opacity-0 group-hover:opacity-10 transition-opacity duration-500 blur-xl`} />
                
                <div className={`w-14 h-14 rounded-xl bg-gradient-to-br ${feature.color} p-3 mb-4 shadow-lg relative`}>
                  <AppIcon name={feature.icon} className="w-full h-full text-white" />
                </div>

                <h3 className="text-xl font-bold text-white mb-2 group-hover:text-cyan-400 transition-colors">
                  {feature.title}
                </h3>
                <p className="text-gray-400 group-hover:text-gray-300 transition-colors">
                  {feature.desc}
                </p>

                <div className="absolute top-0 right-0 w-16 h-16 overflow-hidden rounded-tr-2xl">
                  <div className={`absolute top-0 right-0 w-px h-12 bg-gradient-to-b from-transparent ${feature.color.split(' ')[0]} to-transparent opacity-0 group-hover:opacity-100 transition-opacity`} />
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section id="video-showcase" className="relative pt-16 overflow-hidden">
        <div className="px-[20px] max-w-7xl mx-auto">
          
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
              {t('videoShowcase.title')}
            </h2>
            <p className="text-gray-400 text-lg max-w-2xl mx-auto">
              {t('videoShowcase.subtitle')}
            </p>
          </div>

          <div className="hidden">
            {[1, 2, 3, 4, 5, 6, 7, 8].map((index) => (
              <div key={index} className="video-player-state" data-video-id={index} />
            ))}
          </div>

          <div className="flex flex-col xl:flex-row w-full gap-5 h-[680px] xl:h-[340px]">
            <div className="flex-[1.94] h-full max-h-[340px] overflow-hidden rounded-xl relative">
              <div className="swiper-container relative w-full h-full">
                <div className="swiper-wrapper w-full h-full">
                  {[1, 2, 3].map((index) => (
                    <div key={index} className="swiper-slide w-full h-full">
                      <div className="slide-content w-full h-full">
                        <AutoPlayCarouselVideo
                          src={getLandingVideoSrc(`video${index}.mp4`)}
                          className="w-full h-full object-cover"
                        />
                      </div>
                    </div>
                  ))}
                </div>
                
                <div className="swiper-pagination absolute bottom-4 left-1/2 transform -translate-x-1/2 flex gap-2">
                  {[1, 2, 3].map((_, index) => (
                    <span key={index} className="swiper-pagination-bullet w-2 h-2 rounded-full bg-white/50 cursor-pointer hover:bg-white/80 transition-colors"></span>
                  ))}
                </div>
              </div>
              
              <div className="absolute inset-0 aspect-[240/340] h-full bg-[#111213]/90 border border-[rgba(255,255,255,0.2)] rounded-xl z-10 p-3 flex flex-col gap-1">
                <div className="flex flex-col p-4 cursor-pointer transition-all duration-300 text-white hover:bg-white/5 rounded-lg">
                  <div className="text-base font-medium">{t('videoShowcase.featuredWork.title')}</div>
                  <div className="text-sm text-white/70 mt-2">
                    {t('videoShowcase.featuredWork.desc')}
                  </div>
                  <div className="mt-4 flex justify-end">
                    <button 
                      onClick={() => openAuthModal('signin')}
                      className="text-sm text-white bg-[#383E4E] rounded-full px-4 py-2 hover:bg-[#4D5467] transition-all duration-300"
                    >
                      {t('videoShowcase.featuredWork.cta')}
                    </button>
                  </div>
                </div>
                <div className="flex flex-col p-4 cursor-pointer transition-all duration-300 text-white bg-[#282F3E]/80 rounded-lg hover:bg-[#282F3E]">
                  <div className="text-base font-medium">{t('videoShowcase.characterWork.title')}</div>
                  <div className="text-sm text-white/70 mt-2">
                    {t('videoShowcase.characterWork.desc')}
                  </div>
                  <div className="mt-4 flex justify-end">
                    <button 
                      onClick={() => openAuthModal('signin')}
                      className="text-sm text-white bg-[#383E4E] rounded-full px-4 py-2 hover:bg-[#4D5467] transition-all duration-300"
                    >
                      {t('videoShowcase.characterWork.cta')}
                    </button>
                  </div>
                </div>
              </div>
            </div>
            
            {/* 右侧工具网格 */}
            <div className="flex-[1.06] h-full max-h-[340px] overflow-hidden rounded-xl relative">
              <div className="w-full h-full relative">
                <LazyHomeVideo
                  src={getLandingVideoSrc('video4.mp4')}
                  className="w-full h-full object-cover object-right aspect-[800/340]"
                  autoPlay={false}
                  pauseOnLeave={true}
                />
                
                <div className="absolute inset-0 bg-black/60 flex flex-col justify-between p-6 z-10">
                  <div className="pt-6 pl-4">
                    <div className="text-2xl font-medium text-white">{t('videoShowcase.aiToolbox.title')}</div>
                    <div className="text-base text-white/70">{t('videoShowcase.aiToolbox.subtitle')}</div>
                  </div>
                  <div className="grid grid-cols-2 gap-3 px-6 pb-4">
                    {[
                      { name: t('videoShowcase.aiToolbox.videoGen'), icon: '🎬' },
                      { name: t('videoShowcase.aiToolbox.imageGen'), icon: '🖼️' },
                      { name: t('videoShowcase.aiToolbox.audioGen'), icon: '🎙️' },
                      { name: t('videoShowcase.aiToolbox.characterCreate'), icon: '👤' }
                    ].map((tool) => (
                      <button 
                        key={tool.name} 
                        onClick={() => openAuthModal('signin')}
                        className="tool-item w-full flex items-center justify-center bg-white/10 backdrop-blur-lg rounded-md h-10 cursor-pointer gap-1 hover:bg-white/20 transition-all duration-300"
                      >
                        <span className="text-white">{tool.icon}</span>
                        <div className="tool-name text-sm text-white flex-shrink-0">{tool.name}</div>
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 w-full text-white mt-5 gap-5 xl:grid-cols-6 lg:grid-cols-3 md:grid-cols-2">
            {/* 大广告位 */}
            <div className="w-full h-full max-h-[260px] aspect-[736/260] overflow-hidden rounded-xl lg:col-span-2">
              <div className="swiper-container w-full h-full">
                <div className="swiper-wrapper w-full h-full">
                  <div className="swiper-slide w-full h-full">
                    <LazyHomeVideo
                      src={getLandingVideoSrc('video8.mp4')}
                      className="w-full h-full object-cover"
                    />
                  </div>
                </div>
              </div>
            </div>
            
            {/* 小广告位 */}
            {[6, 7, 5, 10].map((index) => (
              <div 
                key={index} 
                className="ad-item w-full h-full relative rounded-xl overflow-hidden aspect-[368/260] cursor-pointer group/small"
                onTouchStart={(e) => {
                  const video = e.currentTarget.querySelector('video') as HTMLVideoElement | null
                  if (video) {
                    video.currentTime = 0
                    video.play().catch(() => undefined)
                  }
                }}
              >
                <LazyHomeVideo
                  src={getLandingVideoSrc(`video${index}.mp4`)}
                  className="w-full h-full object-cover"
                  autoPlay={false}
                  pauseOnLeave={true}
                  priority="low"
                />
                <div 
                  className="ad-item-content absolute inset-0 w-full h-full bg-[linear-gradient(0deg,#000000_0%,rgba(84,84,84,0)_100%)] flex flex-col items-center justify-end opacity-0 group-hover/small:opacity-100 transition-all duration-300 z-10 pointer-events-none"
                >
                  <button 
                    onClick={() => openAuthModal('signin')}
                    className="ad-item-content-btn text-white/50 border border-white/40 rounded-full w-full max-w-[140px] h-9 flex items-center justify-center text-sm font-bold cursor-pointer hover:bg-white/40 hover:border-white/80 transition-all duration-300 pointer-events-auto"
                  >
                    {t('videoShowcase.createNow')}
                  </button>
                </div>
              </div>
            ))}
            
          </div>

          {/* 瀑布流 */}
          <div className="w-full mt-5 min-h-screen">
            <div className="w-full grid grid-cols-1 xl:grid-cols-6 lg:grid-cols-3 md:grid-cols-2 gap-2">
              {[18, 2, 12, 17, 16, 14, 15, 9, 10, 11, 3, 13].map((index) => (
                <div key={index} className="ad-item w-full h-full relative rounded-xl overflow-hidden aspect-[9/16] cursor-pointer group">
                  <LazyHomeVideo
                    src={getLandingVideoSrc(`video${index}.mp4`)}
                    className="w-full h-full object-cover"
                    autoPlay={false}
                    pauseOnLeave={true}
                    priority="low"
                  />
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="relative py-24 px-4">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
            {t('flyvia')} <span className="bg-gradient-to-r from-cyan-400 to-purple-400 bg-clip-text text-transparent">FLYVIA</span> {t('investment.title').replace('XFX NEXTGEN ', '')}
          </h2>
          <p className="text-gray-400 text-lg max-w-2xl mx-auto mb-2">
            {t('investment.subtitle')}
          </p>
          <p className="text-gray-400 text-lg">
            {t('investment.description')}
          </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="grid sm:grid-cols-2 gap-6">
              {[
                {
                  title: t('investment.benefits.funding.title'),
                  items: [t('investment.benefits.funding.items.0'), t('investment.benefits.funding.items.1'), t('investment.benefits.funding.items.2')]
                },
                {
                  title: t('investment.benefits.promotion.title'),
                  items: [t('investment.benefits.promotion.items.0'), t('investment.benefits.promotion.items.1'), t('investment.benefits.promotion.items.2')]
                },
                {
                  title: t('investment.benefits.ip.title'),
                  items: [t('investment.benefits.ip.items.0'), t('investment.benefits.ip.items.1')]
                },
                {
                  title: t('investment.benefits.tech.title'),
                  items: [t('investment.benefits.tech.items.0'), t('investment.benefits.tech.items.1')]
                }
              ].map((benefit, index) => (
                <div
                  key={index}
                  className="relative p-6 rounded-2xl bg-gradient-to-b from-white/5 to-transparent border border-white/10 hover:border-cyan-500/30 transition-all duration-300 group"
                >
                  <h3 className="text-lg font-bold text-white mb-3 group-hover:text-cyan-400 transition-colors">
                    {benefit.title}
                  </h3>
                  <ul className="space-y-2">
                    {benefit.items.map((item, itemIndex) => (
                      <li key={itemIndex} className="flex items-start gap-2 text-sm text-gray-400">
                        <div className="w-1 h-1 mt-2 flex-shrink-0 bg-purple-400 rounded-full" />
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>

            <div className="relative">
              <div className="relative aspect-video rounded-2xl overflow-hidden border border-white/10">
                <LazyHomeVideo
                  src={getLandingVideoSrc('video3.mp4')}
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
                <div className="absolute bottom-4 left-4 right-4">
                  <p className="text-sm text-cyan-400 mb-1">{t('investment.video.label')}</p>
                  <p className="text-white font-medium">{t('investment.video.title')}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="relative py-32 px-4">
        <div className="max-w-4xl mx-auto text-center relative">
          <div className="absolute inset-0 -z-10">
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[300px] bg-gradient-to-r from-cyan-500/20 via-purple-500/20 to-pink-500/20 rounded-full blur-[100px]" />
          </div>

          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
            {t('ctaTitle')}
          </h2>
          <p className="text-xl text-gray-400 mb-10">
            {t('ctaSubtitle')}
          </p>

          <button
            type="button"
            onClick={() => openAuthModal('signin')}
            className="group relative inline-flex items-center gap-3 px-10 py-5 text-lg font-bold text-black rounded-2xl overflow-hidden"
          >
            <span className="absolute inset-0 bg-gradient-to-r from-cyan-400 via-purple-400 to-pink-400 group-hover:scale-105 transition-transform duration-300" />
            <span className="relative flex items-center gap-3">
              {t('ctaButton')}
              <AppIcon name="arrowRight" className="w-6 h-6 group-hover:translate-x-1 transition-transform" />
            </span>
          </button>
        </div>
      </section>

      <footer className="relative bottom-0 left-0 w-full pt-8 pb-8 bg-black border-t border-white/10">
        <div className="max-w-7xl mx-auto px-6">
          <div className="hidden md:flex flex-col items-center">
            <div className="flex justify-between items-center w-full px-4">
              <div className="flex items-center">
                <Link href={{ pathname: '/' }} className="flex items-center gap-4">
                  <Image
                    src="/logo.webp?v=1"
                    alt="XFX"
                    width={40}
                    height={40}
                    unoptimized
                    className="w-[40px] h-auto"
                  />
                  <span className="text-xl font-bold whitespace-nowrap"
                    style={{
                      background: 'linear-gradient(90deg, #a78bfa 0%, #80ffdb 50%, #42d3ff 100%)',
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent',
                    }}>
                    {t('flyviaFull')}
                  </span>
                </Link>
              </div>
              
            </div>
            <div className="flex flex-col items-center mt-4">
              <span className="text-xs text-gray-400">{t('company')} | {t('companyEn')}</span>
              <span className="mt-1 text-xs text-gray-400">{t('icp')} | <a href="https://beian.miit.gov.cn/#/Integrated/index" target="_blank" rel="noopener noreferrer" className="hover:underline hover:text-white transition">{t('icpBeijing')}</a></span>
            </div>
          </div>

          <div className="md:hidden flex flex-col items-center px-4">
            <div className="flex justify-between items-center w-full mb-4">
              <Link href={{ pathname: '/' }} className="flex items-center gap-3">
                <Image
                  src="/logo.webp?v=1"
                  alt="XFX"
                  width={215}
                  height={40}
                  unoptimized
                  className="w-[40px] h-auto"
                />
                <span className="text-base font-bold whitespace-nowrap"
                  style={{
                    background: 'linear-gradient(90deg, #a78bfa 0%, #80ffdb 50%, #42d3ff 100%)',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                  }}>
                  {t('flyvia')}
                </span>
              </Link>
              
            </div>

            <div className="flex flex-col items-center w-full text-xs text-gray-400 text-center">
              <span className="mb-1">{t('company')}</span>
              <span>{t('icp')} | <a href="https://beian.miit.gov.cn/#/Integrated/index" target="_blank" rel="noopener noreferrer" className="hover:underline">{t('icpBeijing')}</a></span>
            </div>
          </div>
        </div>
      </footer>

      {/* 返回顶部按钮 */}
      <button
        onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
        className={`fixed bottom-6 right-6 md:bottom-10 md:right-10 w-12 h-12 md:w-14 md:h-14 bg-gradient-to-br from-[#0866FF] to-[#00E1FF] rounded-full flex items-center justify-center shadow-lg cursor-pointer z-50 transition-all duration-300 hover:shadow-[0_0_20px_rgba(8,102,255,0.5)] hover:scale-110 ${
          scrollY > 500 ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10 pointer-events-none'
        }`}
        aria-label={t('backToTop')}
      >
        <AppIcon name="chevronUp" className="w-5 h-5 text-white" />
      </button>
      <Suspense fallback={null}>
        {showSignInModal && <SignInModal open={showSignInModal} initialError={authError} onClose={closeAuthModal} />}
      </Suspense>
    </div>
  )
}
